import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";

import { useSearch } from "@tanstack/solid-router";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { Button, buttonVariants } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { api, createApiQuery, SERVER_BASE_URL } from "@/lib/api";
import { isTauriRuntime } from "@/lib/platform";
import { queryClient } from "@/lib/query-client";
import { type components, queryKeys, sharedApi } from "@listto/shared";
import { showToast } from "@/components/ui/toast";
import { Link2, ShieldCheck } from "@fleet/icons";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ProviderId = "google" | "apple";

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: "Google",
  apple: "Apple",
};

/**
 * "Connected accounts": lets you link/unlink Google and Apple from the profile.
 * It only shows providers ENABLED in the backend. It respects the last-method
 * safeguard (the backend rejects leaving the user without access).
 */
export function ConnectedAccountsSection() {
  const { t } = useI18n();
  // Read ?linked=<provider> to show feedback after returning from the callback.
  const search = useSearch({ strict: false }) as () => { linked?: string };
  const [confirmUnlink, setConfirmUnlink] = createSignal<ProviderId | null>(null);
  const [busy, setBusy] = createSignal(false);

  /**
   * P23/CC-60 · I3 — same reason as `methodsQuery`: `openapi-fetch` NEVER
   * throws, so a `?? { google: false, apple: false }` turned a 500 into
   * a SUCCESS asserting "no social login configured". Since the entire
   * card is behind `enabledProviders().length > 0`, the result was not an
   * absent error box: it was **the section disappearing without saying anything**.
   *
   * NOTE: this query shares `queryKey` with `components/auth/social-auth-buttons.tsx`.
   * Fixing ONLY this file does not close the defect — see the comment over there.
   */
  const providersQuery = createApiQuery<
    { google?: boolean; apple?: boolean },
    components["schemas"]["ErrorModel"]
  >(
    () => queryKeys.auth.socialProviders(),
    async () => sharedApi.socialProviders(api),
    () => ({ staleTime: 5 * 60 * 1000, retry: false }),
  );

  /**
   * P23/CC-60 · I3 — this `queryFn` CANNOT swallow the error.
   *
   * Before, it was a raw `useQuery` doing
   * `return data ?? { providers: [], has_password: false }` over
   * `openapi-fetch`, a client that NEVER throws: it returns `{data, error}`. With
   * `/api/auth/login-methods` down, `data` came back `undefined`, the default
   * object was returned and the query resolved to **SUCCESS**. Consequences, both
   * measured with a forced 500 in browser:
   *
   *  1. `methodsQuery.isError` was **unreachable** → the `QueryErrorState` of
   *     below was DEAD CODE: applied in the diff, green in `tsc`, never
   *     rendered.
   *  2. The `isSuccess` guard of I2 did not protect anything either — the state WAS
   *     success — so the card asserted "Google · Not connected" and "This
   *     is your only sign-in method" for an account that might indeed be
   *     linked. The exact falsehood that I2/I3 exist to kill.
   *
   * `createApiQuery` goes through `handleApiResponse`, which THROWS on `error` or
   * on an empty body: that is what makes the error branch reachable.
   */
  const methodsQuery = createApiQuery<
    { providers?: string[]; has_password?: boolean },
    components["schemas"]["ErrorModel"]
  >(
    () => queryKeys.auth.loginMethods(),
    async () => sharedApi.loginMethods(api),
    () => ({ retry: false }),
  );

  // P23/CC-60 · I2: `.data` ONLY in `isSuccess`. Before, it was enough for the
  // value not to be `undefined`, so a network error gave "zero providers" and
  // the whole card disappeared without saying why.
  const enabledProviders = createMemo<ProviderId[]>(() => {
    if (!providersQuery.isSuccess) {
      return [];
    }
    const p = providersQuery.data;
    if (!p) {
      return [];
    }
    const list: ProviderId[] = [];
    if (p.google) {
      list.push("google");
    }
    if (p.apple) {
      list.push("apple");
    }
    return list;
  });

  // P23/CC-60 · I2: same. If `login-methods` fails, `isLinked()` said "not
  // linked" for an account that IS linked: that is not data, it is a lie.
  const linkedProviders = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.providers ?? []) : [],
  );
  const hasPassword = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.has_password ?? false) : false,
  );

  const isLinked = (provider: ProviderId) => linkedProviders().includes(provider);

  // Total number of access methods (password + each linked social account).
  const totalMethods = createMemo(() => linkedProviders().length);

  // Is this the LAST method? (it cannot be unlinked without establishing another).
  const isLastMethod = (provider: ProviderId) =>
    isLinked(provider) && totalMethods() <= 1 && !hasPassword();

  /**
   * `client` tells the server which client starts the link: a browser gets its
   * callback without the session token (`native` keeps it for the Tauri WebView,
   * which authenticates with it). See `native_client` in the server's `handlers/auth.rs`.
   */
  const linkUrl = (provider: ProviderId) => {
    const base = `${SERVER_BASE_URL.replace(/\/$/, "")}/api/auth/link-social/${provider}`;
    const query = new URLSearchParams({ client: isTauriRuntime() ? "native" : "web" });
    return `${base}?${query.toString()}`;
  };

  const handleUnlink = async (provider: ProviderId) => {
    setBusy(true);
    try {
      const { error } = await sharedApi.unlinkAccount(api, provider);
      if (error) {
        const message = error.message ?? "";
        if (message.includes("only sign-in method")) {
          showToast(t(msg`Set a password before unlinking your only sign-in method.`), "error");
        } else {
          showToast(t(msg`Could not unlink the account. Please try again.`), "error");
        }
        return;
      }
      showToast(t(msg`Account unlinked.`), "success");
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    } finally {
      setBusy(false);
      setConfirmUnlink(null);
    }
  };

  // Feedback when returning from the linking flow (?linked=<provider>).
  let linkedFeedbackDone = false;
  createEffect(() => {
    const provider = search().linked;
    if (provider && !linkedFeedbackDone) {
      linkedFeedbackDone = true;
      showToast(t(msg`Account connected.`), "success");
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    }
  });

  return (
    // P23/CC-60 · I3: the section is mounted ALSO when we don't know what is there.
    // Hiding it upon a providers catalog failure is lying by
    // omission: the user cannot distinguish "this app has no social login"
    // from "the server did not respond".
    <Show when={enabledProviders().length > 0 || providersQuery.isError}>
      <Card>
        <CardHeader class="flex flex-row items-center gap-2 space-y-0">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Connected accounts`)}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-3">
          {/* P23/CC-60 · I3: if we don't know what is linked, it is stated — we don't
              render «Not connected» for all providers. */}
          <Show when={providersQuery.isError || methodsQuery.isError}>
            <QueryErrorState
              testId="connected-accounts-error"
              compact
              title={t(msg`Couldn't load your sign-in methods`)}
              onRetry={() => {
                if (providersQuery.isError) {
                  void providersQuery.refetch();
                }
                if (methodsQuery.isError) {
                  void methodsQuery.refetch();
                }
              }}
            />
          </Show>
          <Show
            when={!methodsQuery.isPending && !methodsQuery.isError}
            fallback={
              <Show when={!methodsQuery.isError}>
                <div class="space-y-3">
                  <Skeleton class="h-14 w-full rounded-xl" />
                  <Skeleton class="h-14 w-full rounded-xl" />
                </div>
              </Show>
            }
          >
            <For each={enabledProviders()}>
              {(provider) => (
                <div class="flex items-center gap-4 rounded-xl border border-border bg-muted/30 p-4">
                  <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <Link2 class="h-5 w-5" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-semibold">{PROVIDER_LABELS[provider]}</p>
                    <Show
                      when={isLinked(provider)}
                      fallback={
                        <p class="text-xs text-muted-foreground">{t(msg`Not connected`)}</p>
                      }
                    >
                      <Badge
                        variant="secondary"
                        class="mt-1 h-5 border-none bg-primary/20 px-1.5 text-[10px] font-bold uppercase tracking-wider text-primary"
                      >
                        {t(msg`Connected`)}
                      </Badge>
                    </Show>
                  </div>

                  <Show
                    when={isLinked(provider)}
                    fallback={
                      <a
                        href={linkUrl(provider)}
                        class={buttonVariants({ variant: "outline", size: "sm" })}
                        data-testid={`link-${provider}`}
                      >
                        {t(msg`Connect`)}
                      </a>
                    }
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy() || isLastMethod(provider)}
                      title={
                        isLastMethod(provider)
                          ? t(msg`Set a password before unlinking your only sign-in method.`)
                          : undefined
                      }
                      onClick={() => setConfirmUnlink(provider)}
                      class="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      {t(msg`Disconnect`)}
                    </Button>
                  </Show>
                </div>
              )}
            </For>

            <Show when={!hasPassword() && totalMethods() <= 1}>
              <p class="text-xs text-muted-foreground">
                {t(
                  msg`This is your only sign-in method. Set a password to be able to disconnect it.`,
                )}
              </p>
            </Show>
          </Show>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmUnlink() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUnlink(null);
          }
        }}
        title={t(msg`Disconnect account?`)}
        description={t(
          msg`You will no longer be able to sign in with this provider. You can reconnect it anytime.`,
        )}
        confirmLabel={t(msg`Disconnect`)}
        cancelLabel={t(msg`Cancel`)}
        onConfirm={() => {
          const provider = confirmUnlink();
          if (provider) {
            void handleUnlink(provider);
          }
        }}
      />
    </Show>
  );
}
